import { __awaiter } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Popover, PopoverHeight, PopoverType, ProductWizardHeader } from '@as/cotopaxi';
import { BUCKET_FILTER, ProductWizardFlowType, ProductWizardInputType, RANGE_FILTER, } from '@as-react/common-types';
import { isNullOrUndefined } from '@as-react/common-utils';
import { useEffect, useState } from 'react';
import { getBucketFilter, getFilterQueryParameter, getMultiFilter, getRangeFilter } from '../../util/FilterUtil';
import { fixedEncodeURIComponent } from '../../util/UrlUtil';
import { ProductWizardFlowSelectorFooter } from './ProductWizardFlowSelectorFooter';
import { ProductWizardIntro } from './ProductWizardIntro';
import { ProductWizardQuestionList } from './ProductWizardQuestionList';
import { ProductWizardQuestionsFooter } from './ProductWizardQuestionsFooter';
var ProductWizardView;
(function (ProductWizardView) {
    ProductWizardView[ProductWizardView["INTRO"] = 0] = "INTRO";
    ProductWizardView[ProductWizardView["FLOW_SELECTOR"] = 1] = "FLOW_SELECTOR";
    ProductWizardView[ProductWizardView["QUESTIONS"] = 2] = "QUESTIONS";
})(ProductWizardView || (ProductWizardView = {}));
export const ProductWizard = ({ anchorEl, loggedIn, userFirstName, productWizardData, handlePopoverClose, fetchSearchResults, }) => {
    var _a;
    const [flowOptions, setFlowOptions] = useState(productWizardData.question);
    const [selectedFlowId, setSelectedFlowId] = useState(null);
    const [selectedFlow, setSelectedFlow] = useState(null);
    const [questionIndex, setQuestionIndex] = useState(null);
    const [filter, setFilter] = useState(null); // include category and all questions answers
    const [totalProductsCount, setTotalProductsCount] = useState(0);
    const [showLoadingIcon, setShowLoadingIcon] = useState(false);
    const [view, setView] = useState(ProductWizardView.INTRO);
    const getQuestionFilter = (question) => {
        let filterValue = '';
        const selectedAnswers = question.answers.filter((answer) => answer.selected);
        if (selectedAnswers.length) {
            if (question.filterType === RANGE_FILTER) {
                filterValue = getRangeFilter(question.filter, selectedAnswers[0].min, selectedAnswers[0].max);
            }
            else if (question.filterType === BUCKET_FILTER) {
                filterValue = getBucketFilter(question.filter, selectedAnswers[0].appliedFilters[0]);
            }
            else {
                filterValue = getMultiFilter(question.filter, selectedAnswers.map((answer) => answer.appliedFilters.join(';')));
            }
        }
        return filterValue;
    };
    const getFilterQueryParams = (questions) => questions.reduce((query, question) => `${query}${getQuestionFilter(question)}`, getFilterQueryParameter(selectedFlow.categoryFilter));
    const loadFlowOptionsProductsCount = () => __awaiter(void 0, void 0, void 0, function* () {
        setShowLoadingIcon(true);
        const results = yield Promise.all(flowOptions.answers.map((answer) => fetchSearchResults(answer.categoryFilter)));
        flowOptions.dataLoaded = true;
        flowOptions.answers.forEach((answer, index) => (answer.productCount = results[index]));
        setFlowOptions(Object.assign({}, flowOptions));
        setShowLoadingIcon(false);
    });
    const loadQuestionOptionsProductsCount = () => __awaiter(void 0, void 0, void 0, function* () {
        setShowLoadingIcon(true);
        const baseFilter = getFilterQueryParams(selectedFlow.questions.slice(0, questionIndex));
        const question = selectedFlow.questions[questionIndex];
        const results = yield Promise.all(question.answers.map((answer) => {
            let answerFilter = '';
            if (question.filterType === RANGE_FILTER) {
                answerFilter = getRangeFilter(question.filter, answer.min, answer.max);
            }
            else if (question.filterType === BUCKET_FILTER) {
                answerFilter = getBucketFilter(question.filter, answer.appliedFilters[0]);
            }
            else {
                answerFilter = getMultiFilter(question.filter, answer.appliedFilters);
            }
            return fetchSearchResults(`${baseFilter}${answerFilter}`);
        }));
        question.dataLoaded = true;
        question.answers.forEach((answer, index) => (answer.productCount = results[index]));
        setSelectedFlow(Object.assign({}, selectedFlow));
        setShowLoadingIcon(false);
    });
    const loadTotalProductsCount = (_filter) => __awaiter(void 0, void 0, void 0, function* () {
        const productsCount = yield fetchSearchResults(_filter);
        setTotalProductsCount(productsCount || 0);
    });
    const isMultiFlow = () => productWizardData.type === ProductWizardFlowType.MULTIPLE && productWizardData.flows.length > 1;
    const setFlowView = () => {
        if (isMultiFlow()) {
            setView(ProductWizardView.FLOW_SELECTOR);
            loadFlowOptionsProductsCount();
        }
        else {
            setSelectedFlow(productWizardData.flows[0]);
            setQuestionIndex(0);
            setView(ProductWizardView.QUESTIONS);
        }
    };
    const onFlowChange = (answerIndex) => {
        flowOptions.answers.forEach((answer, index) => (answer.selected = index === answerIndex));
        setSelectedFlowId(flowOptions.answers[answerIndex].id);
        setFlowOptions(Object.assign({}, flowOptions));
        setTotalProductsCount(0);
    };
    const resetQuestionState = (question) => {
        question.dataLoaded = false;
        question.answers.forEach((answer) => {
            answer.selected = false;
            answer.productCount = undefined;
        });
    };
    const onAnswerChange = (answerIndex) => {
        const questions = selectedFlow.questions;
        const question = questions[questionIndex];
        if (question.inputType === ProductWizardInputType.CHECKBOX) {
            question.answers[answerIndex].selected = !question.answers[answerIndex].selected;
        }
        else {
            question.answers.forEach((answer, index) => (answer.selected = index === answerIndex));
        }
        // clear products count and deselect answers in next questions
        for (let i = questionIndex + 1; i < questions.length; i++) {
            resetQuestionState(questions[i]);
        }
        setSelectedFlow(Object.assign({}, selectedFlow));
    };
    const setQuestionsView = () => {
        const flow = productWizardData.flows.find((item) => item.id === selectedFlowId);
        setSelectedFlow(flow);
        setQuestionIndex(0);
        setView(ProductWizardView.QUESTIONS);
    };
    const isLastQuestion = () => questionIndex === selectedFlow.questions.length - 1;
    const isFirstQuestion = () => questionIndex === 0;
    const selectNextQuestion = () => {
        !isLastQuestion() && setQuestionIndex(questionIndex + 1);
    };
    const goToPrevStep = () => {
        if (!isFirstQuestion()) {
            setQuestionIndex(questionIndex - 1);
        }
        else {
            if (isMultiFlow()) {
                setQuestionIndex(null);
                setFilter(null);
                setView(ProductWizardView.FLOW_SELECTOR);
            }
        }
    };
    const resetWizardState = () => {
        selectedFlow.questions.forEach((question) => resetQuestionState(question));
        if (isMultiFlow()) {
            resetQuestionState(flowOptions);
            setFlowOptions(Object.assign({}, flowOptions));
            setQuestionIndex(null);
            setSelectedFlowId(null);
            setSelectedFlow(null);
            setView(ProductWizardView.FLOW_SELECTOR);
            loadFlowOptionsProductsCount();
        }
        else {
            isFirstQuestion() ? loadQuestionOptionsProductsCount() : setQuestionIndex(0);
        }
        setFilter(null);
        setTotalProductsCount(0);
    };
    const shouldShowBackBtn = () => view === ProductWizardView.QUESTIONS ? (isMultiFlow() ? true : !isFirstQuestion()) : false;
    const getFooterElement = () => {
        if (view === ProductWizardView.FLOW_SELECTOR) {
            return (_jsx(ProductWizardFlowSelectorFooter, { activeStateLabel: flowOptions.nextButtonLabel, disabledStateLabel: flowOptions.nonActiveStatLabel, onClick: setQuestionsView, disabled: !selectedFlowId }));
        }
        else if (view === ProductWizardView.QUESTIONS) {
            // Server expects double encoded filters inside the url
            const doubleEncodedFilter = fixedEncodeURIComponent(filter);
            return (_jsx(ProductWizardQuestionsFooter, { showResetBtn: isMultiFlow() || !isFirstQuestion(), question: selectedFlow.questions[questionIndex], lastQuestion: isLastQuestion(), onResetBtnClick: resetWizardState, onNexBtnClick: selectNextQuestion, totalProductsCount: totalProductsCount, resultsUrl: `${selectedFlow.categoryUrl}?filter=${doubleEncodedFilter}` }));
        }
        return null;
    };
    const popoverHeader = (_jsx(ProductWizardHeader, { questionsCount: (_a = selectedFlow === null || selectedFlow === void 0 ? void 0 : selectedFlow.questions) === null || _a === void 0 ? void 0 : _a.length, activeQuestion: questionIndex + 1, showBackButton: shouldShowBackBtn(), showStepper: view === ProductWizardView.QUESTIONS, onBackBtnClick: goToPrevStep, onCloseBtnClick: handlePopoverClose }));
    useEffect(() => {
        if (selectedFlow) {
            const filterQuery = getFilterQueryParams(selectedFlow.questions);
            const shouldLoadTotalProductsCount = filter !== filterQuery;
            if (shouldLoadTotalProductsCount) {
                setFilter(filterQuery);
                loadTotalProductsCount(filterQuery);
            }
        }
    }, [selectedFlow]);
    useEffect(() => {
        if (questionIndex !== null && selectedFlow) {
            const shouldLoadQuestionOptionsProductsCount = isNullOrUndefined(selectedFlow.questions[questionIndex].answers[0].productCount);
            shouldLoadQuestionOptionsProductsCount && loadQuestionOptionsProductsCount();
        }
    }, [questionIndex]);
    return (_jsxs(Popover, { withBackground: true, loading: showLoadingIcon, height: view !== ProductWizardView.INTRO && PopoverHeight.LARGE, customHeader: popoverHeader, footerElement: getFooterElement(), anchorEl: anchorEl, type: PopoverType.MODAL, onClose: handlePopoverClose, children: [productWizardData && view === ProductWizardView.INTRO && (_jsx(ProductWizardIntro, { title: productWizardData.intro.title, subTitle: productWizardData.intro.subTitle, buttonLabel: productWizardData.intro.buttonLabel, loggedIn: loggedIn, userFirstName: userFirstName, startProductWizard: setFlowView })), flowOptions && view === ProductWizardView.FLOW_SELECTOR && (_jsx(ProductWizardQuestionList, { title: flowOptions.title, subTitle: flowOptions.subTitle, inputType: ProductWizardInputType.RADIO, answers: flowOptions.answers, showAnswers: flowOptions.dataLoaded, onSelectChange: onFlowChange })), productWizardData && view === ProductWizardView.QUESTIONS && (_jsx(ProductWizardQuestionList, { title: selectedFlow.questions[questionIndex].title, subTitle: selectedFlow.questions[questionIndex].subTitle, inputType: selectedFlow.questions[questionIndex].inputType, answers: selectedFlow.questions[questionIndex].answers, showAnswers: selectedFlow.questions[questionIndex].dataLoaded, onSelectChange: onAnswerChange }))] }));
};
